import { useSortable } from '@dnd-kit/sortable'
import { Controller, FieldArrayWithId } from 'react-hook-form'
import { Step4FormOptionsProps, Step4FormProps, WizzardStep4Form } from '../types'
import { CSS } from '@dnd-kit/utilities'
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    ClassNameMap,
    FormControlLabel,
    FormLabel,
    Grid,
    MenuItem,
    TextField,
} from '@mui/material'
import { DragIndicator, Clear } from '@mui/icons-material'
import FormItem from '../../../hoc/FormItem'
import HelperModal from '../../../hoc/HelperModal'

interface ContactItemProps {
    id: string
    field: FieldArrayWithId<WizzardStep4Form, 'personContacts'>
    index: number
    classes: ClassNameMap<'flex' | 'link' | 'longLabelText' | 'divider' | 'zipAutocomplete' | 'reactionBox' | 'personCard'>
    form: Step4FormProps
    options: Step4FormOptionsProps
}

export const ContactItem = (props: ContactItemProps) => {
    const { id, field, index, classes, form, options } = props
    const { control, formState, getValues } = form.form
    const { errors } = formState
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })
    const hasOneCreatableAccount =
        getValues('personContacts')?.filter((contact) => contact.creatAccountToZZ === true).length === 1

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }
    return (
        <Grid ref={setNodeRef} style={style} item key={field.id} xs={12} md={6}>
            <Card
                variant='outlined'
                className={classes.personCard}
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <CardHeader
                    title={`Kontaktní osoba č. ${index + 1}`}
                    action={
                        <>
                            <Box component='span' sx={{ mr: index === 0 ? 0 : 1, cursor: 'grab' }} {...attributes} {...listeners}>
                                <DragIndicator />
                            </Box>
                            {index !== 0 && (
                                <Button
                                    variant='contained'
                                    size='small'
                                    color='error'
                                    sx={{ mt: '-4px', boxShadow: 'none' }}
                                    onClick={() => (index !== 0 ? form.contactPersons.remove(index) : null)}
                                >
                                    <Clear />
                                </Button>
                            )}
                        </>
                    }
                />
                <CardContent>
                    <Grid container marginBottom={1}>
                        <Grid item xs={12} sm={4}>
                            <FormItem>
                                <FormLabel>Jméno a příjmení</FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <FormItem>
                                <Controller
                                    name={`personContacts.${index}.fullName` as const}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type='text'
                                            fullWidth
                                            error={
                                                errors.personContacts &&
                                                errors.personContacts[index] &&
                                                errors.personContacts[index].fullName !== undefined
                                            }
                                            helperText={
                                                errors.personContacts &&
                                                errors.personContacts[index] &&
                                                errors.personContacts[index].fullName
                                                    ? errors.personContacts[index].fullName?.message
                                                    : undefined
                                            }
                                            label=''
                                        />
                                    )}
                                />
                            </FormItem>
                        </Grid>
                    </Grid>
                    <Grid container marginBottom={1}>
                        <Grid item xs={12} sm={4}>
                            <FormItem>
                                <FormLabel>E-mail</FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <FormItem>
                                <Controller
                                    name={`personContacts.${index}.email` as const}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            value={field.value || ''}
                                            type='email'
                                            fullWidth
                                            error={
                                                errors.personContacts &&
                                                errors.personContacts[index] &&
                                                errors.personContacts[index].email !== undefined
                                            }
                                            helperText={
                                                errors.personContacts &&
                                                errors.personContacts[index] &&
                                                errors.personContacts[index].email
                                                    ? errors.personContacts[index].email?.message
                                                    : undefined
                                            }
                                            label=''
                                            onBlur={() => form.contactPersons.update()}
                                        />
                                    )}
                                />
                            </FormItem>
                        </Grid>
                    </Grid>
                    <Grid container marginBottom={1}>
                        <Grid item xs={12} sm={4}>
                            <FormItem>
                                <FormLabel>Telefon</FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <FormItem>
                                <Controller
                                    name={`personContacts.${index}.phone` as const}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type='text'
                                            fullWidth
                                            error={
                                                errors.personContacts &&
                                                errors.personContacts[index] &&
                                                errors.personContacts[index].phone !== undefined
                                            }
                                            helperText={
                                                errors.personContacts &&
                                                errors.personContacts[index] &&
                                                errors.personContacts[index].phone
                                                    ? errors.personContacts[index].phone?.message
                                                    : undefined
                                            }
                                            label=''
                                        />
                                    )}
                                />
                            </FormItem>
                        </Grid>
                    </Grid>
                    <Grid container marginBottom={1}>
                        <Grid item xs={12} sm={4}>
                            <FormItem>
                                <FormLabel>
                                    Oprávnění &ensp;
                                    <HelperModal textKey='CONTACT_PERSON_ROLE' />
                                </FormLabel>
                            </FormItem>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <FormItem>
                                <Controller
                                    name={`personContacts.${index}.role` as const}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            type='text'
                                            fullWidth
                                            select
                                            error={
                                                errors.personContacts &&
                                                errors.personContacts[index] &&
                                                errors.personContacts[index].role !== undefined
                                            }
                                            helperText={
                                                errors.personContacts &&
                                                errors.personContacts[index] &&
                                                errors.personContacts[index].role
                                                    ? errors.personContacts[index].role?.message
                                                    : undefined
                                            }
                                            label=''
                                        >
                                            {options.contactPersonRoles.map((role, index) => (
                                                <MenuItem key={index} value={role.value}>
                                                    {role.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                />
                            </FormItem>
                        </Grid>
                    </Grid>
                    <Grid container marginBottom={1}>
                        <Grid item sm={4}></Grid>
                        <Grid item sm={8}>
                            <FormItem>
                                <Controller
                                    name={`personContacts.${index}.creatAccountToZZ` as const}
                                    control={control}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    {...field}
                                                    checked={field.value}
                                                    disabled={hasOneCreatableAccount && field.value}
                                                />
                                            }
                                            label='vytvořit účet v zákaznické zóně'
                                        />
                                    )}
                                />
                            </FormItem>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Grid>
    )
}
